import { useCallback } from 'react';

import { useDxfViewerSession } from '../DxfViewerContext';
import type { SelectionGranularity } from '../session/types';
import { useReadableStore } from '../session/useSessionState';

export type DxfSelectionModeState = {
  activeGranularity: SelectionGranularity;
  isInEffect: boolean;
  onSelectGranularity: (granularity: SelectionGranularity) => void;
};

/**
 * Connects `DxfSelectionModeGroup` to the session.
 *
 * Its own hook because both toolbars render the group and neither should own the
 * "picking a selection mode also leaves Measure" rule — that has to match the
 * V/A shortcuts exactly, and the input controller applies the same two writes.
 */
export const useDxfSelectionMode = (): DxfSelectionModeState => {
  const session = useDxfViewerSession();
  const activeGranularity = useReadableStore(session.selectionGranularity);
  const tool = useReadableStore(session.tools);

  const onSelectGranularity = useCallback(
    (granularity: SelectionGranularity) => {
      session.selectionGranularity.set(granularity);
      // Measure only, matching the V/A shortcuts. An armed brush picks through
      // the same `pickAt` that Choose does, so changing granularity retunes the
      // brush instead of putting it down; Measure ignores geometry, so there
      // this is the way back to selecting.
      if (session.tools.getState() === 'measure') {
        session.tools.set('select');
      }
    },
    [session],
  );

  return {
    activeGranularity,
    // Granularity is in force under Choose *and* under every brush — both pick
    // through `pickAt`. Measure is the only tool that suspends it.
    isInEffect: tool !== 'measure',
    onSelectGranularity,
  };
};
